import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../../models/user.model';
import { onError } from '../../../utils/error.util';
import { getUser, updateUser, createUser } from '../../../services/users.service';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Permission, POSITION_OPTIONS } from '../../../models/role.model';

const UserSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  givenName: Yup.string(),
  familyName: Yup.string(),
});

const userInitialValues = {
  email: '',
  givenName: '',
  familyName: '',
  position: '',
  permissions: [] as Permission[],
};

export default function UserFormContainer() {
  const navigate = useNavigate();
  const { username } = useParams();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function onLoad() {
      if (!username) {
        setIsLoading(false);
        return;
      }

      if (username === 'new') {
        setIsLoading(false);
        return;
      }

      try {
        const response = await getUser(decodeURIComponent(username));
        setUser(response);
      } catch (e) {
        onError(e);
      }

      setIsLoading(false);
    }

    onLoad();
  }, [username]);

  const getInitialValues = () => {
    if (username === 'new') {
      return userInitialValues;
    }

    if (!user) {
      return userInitialValues;
    }

    const email = user.attributes.find((attr) => attr.name === 'email')?.value || '';
    const givenName = user.attributes.find((attr) => attr.name === 'given_name')?.value || '';
    const familyName = user.attributes.find((attr) => attr.name === 'family_name')?.value || '';
    const position = user.attributes.find((attr) => attr.name === 'custom:position')?.value || '';
    const permissionsStr = user.attributes.find((attr) => attr.name === 'custom:permissions')?.value || '';
    const permissions = permissionsStr ? permissionsStr.split(',').filter((p) => p) : [];

    return {
      email,
      givenName,
      familyName,
      position,
      permissions: permissions as Permission[],
    };
  };

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      const attributes: Array<{ name: string; value: string }> = [{ name: 'email', value: values.email }];

      if (values.givenName) {
        attributes.push({ name: 'given_name', value: values.givenName });
      }
      if (values.familyName) {
        attributes.push({ name: 'family_name', value: values.familyName });
      }
      // Always include position when updating (even if empty to clear it)
      if (username !== 'new') {
        attributes.push({ name: 'custom:position', value: values.position || '' });
      } else if (values.position) {
        // Only include position for new users if it has a value
        attributes.push({ name: 'custom:position', value: values.position });
      }
      // Always include permissions when updating (even if empty to clear them)
      if (username !== 'new') {
        attributes.push({ name: 'custom:permissions', value: values.permissions?.join(',') || '' });
      }

      if (username === 'new') {
        await createUser({
          email: values.email,
          givenName: values.givenName,
          familyName: values.familyName,
          position: values.position,
          permissions: values.permissions?.join(',') || '',
        });
      } else {
        await updateUser(decodeURIComponent(username!), attributes);
      }
      navigate('/users');
    } catch (e: any) {
      onError(e);
      setIsSubmitting(false);
    }
  };

  function renderForm() {
    return (
      <Container>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={UserSchema}
          onSubmit={onSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <Box sx={{ mb: 3 }}>
                <h1>{username === 'new' ? 'Create User' : 'Edit User'}</h1>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="givenName"
                    name="givenName"
                    label="First Name"
                    value={values.givenName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="familyName"
                    name="familyName"
                    label="Last Name"
                    value={values.familyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    disabled={username !== 'new'}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="position-label">Position</InputLabel>
                    <Select
                      labelId="position-label"
                      id="position"
                      name="position"
                      value={values.position || ''}
                      label="Position"
                      onChange={(e) => setFieldValue('position', e.target.value)}
                      onBlur={handleBlur}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {Object.values(POSITION_OPTIONS).map((position) => (
                        <MenuItem key={position} value={position}>
                          {position}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    multiple
                    id="permissions"
                    options={Object.values(Permission).sort()}
                    value={values.permissions}
                    onChange={(_, newValue) => {
                      setFieldValue('permissions', newValue);
                    }}
                    getOptionLabel={(option) => option.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    filterOptions={(options, state) => {
                      // Filter out already selected permissions
                      const availableOptions = options.filter((option) => !values.permissions.includes(option));
                      // Apply default filtering by search text
                      const searchText = state.inputValue?.toLowerCase() || '';
                      if (!searchText) {
                        return availableOptions;
                      }
                      return availableOptions.filter((option) => option.toLowerCase().includes(searchText));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Permissions"
                        placeholder="Select permissions"
                        helperText="Select one or more permissions for this user"
                      />
                    )}
                  />
                </Grid>

                {username !== 'new' && user && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                      <h3>User Status</h3>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Status: ${user.userStatus}`}
                          color={user.userStatus === 'CONFIRMED' ? 'success' : 'default'}
                          size="small"
                        />
                        <Chip
                          label={user.enabled ? 'Active' : 'Inactive'}
                          color={user.enabled ? 'success' : 'default'}
                          size="small"
                        />
                        {user.userCreateDate && (
                          <Chip label={`Created: ${new Date(user.userCreateDate).toLocaleDateString()}`} size="small" />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/users')} disabled={isSubmitting}>
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Container>
    );
  }

  return <main id="main">{isLoading ? <CircularProgressComponent /> : renderForm()}</main>;
}

